import z from 'zod';
import { paginationParamsSchema, PaginatedResponseBuilder, responseBaseSchema } from '../shared.types';

// ---- Primitives ----

const linkSchema = z.looseObject({
    id: z.number().int().optional(),
    locationTaskId: z.number().int().optional(),
    baselineTaskLinkId: z.number().int().nullable().optional(),
    customerFileId: z.number().int().nullable().optional(),
    ordinal: z.number().int().nullable().optional(),
    url: z.string().nullable().optional(),
    name: z.string(),
    editingName: z.string().optional().default(''),
    editingUrl: z.string().optional().default(''),
    isActive: z.boolean().optional().default(true),
});

const fileSchema = z.looseObject({
    id: z.number().int(),
    locationTaskId: z.number().int().optional(),
    fileName: z.string().default(''),
    name: z.string().default(''),
    editingName: z.string().optional().default(''),
    description: z.string().nullable().optional().default(''),
    editingDescription: z.string().optional().default(''),
    extension: z.string().default(''),
    fileStatusId: z.number().int().default(0),
    storageName: z.string().nullable().optional().default(''),
    isActive: z.boolean().optional().default(true),
    imageObjectURL: z.string().optional().default(''),
    file: z.instanceof(File).nullable().optional().default(null),
    showFileInput: z.boolean().optional().default(false),
});

const prereqSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    coreStatusName: z.string().nullable().optional(),
    statusId: z.number().int().nullable().optional(),
});

const stepSchema = z.looseObject({
    id: z.number().int(),
    name: z.string(),
    description: z.string().nullable().optional(),
    isActive: z.boolean().optional().default(true),
    isCompleted: z.boolean().optional().default(false),
    ordinal: z.number().int().optional(),
    baselineTaskStepId: z.number().int().nullable().optional(),
    locationTaskId: z.number().int().nullable().optional(),
});

// Filter dropdown options: id + name, with optional styles for the tag chip
const tagSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    styles: z
        .object({
            backgroundColor: z.string(),
            color: z.string(),
        })
        .nullable()
        .optional(),
});

// Tags carried on a task row: the search endpoint flattens the tag to its display
// values and sends no id, so this cannot reuse the filter-option shape above.
const taskRowTagSchema = z.looseObject({
    name: z.string(),
    backgroundColor: z.string().nullable().optional(),
    fontColor: z.string().nullable().optional(),
});

const filterOptionSchema = z.object({
    id: z.number().int(),
    name: z.string(),
});

const statusFilterOptionSchema = filterOptionSchema.extend({
    coreStatusId: z.number().int().nullable().optional(),
});

const columnSchema = z.object({
    columnId: z.number().int(),
    fieldName: z.string(),
    displayName: z.string(),
    isSortable: z.boolean().nullable().optional(),
    isDefault: z.boolean().nullable().optional(),
});

const customFilterSchema = z.object({
    filterName: z.string(),
    ordinal: z.number().int().nullable().optional(),
    isCustom: z.boolean().nullable().optional(),
    isDefault: z.boolean().nullable().optional(),
});

// ---- Location ----

export const locationSchema = z.object({
    id: z.union([z.number().int(), z.string()]),
    locationKey: z.string(),
    name: z.string(),
    abbreviation: z.string().nullable().optional(),
    isProject: z.boolean().nullable().optional(),
});

// ---- LocationTask ----

const lastCommentSchema = z.looseObject({
    comment: z.string().nullable().optional(),
    commentHtml: z.string().nullable().optional(),
    createdDate: z.string().nullable().optional(),
    createdBy: z.string().nullable().optional(),
});

// Loose object: the list view renders many additional server-driven columns
// (statusName, targetDaysOut, discipline, contingentId, ...) that must pass
// through the parse instead of being stripped.
export const locationTaskSchema = z.looseObject({
    id: z.number().int(),
    name: z.string(),
    locationKey: z.string().optional().default(''),
    locationName: z.string().nullable().optional(),
    targetDate: z.string().nullable().optional(),
    startDate: z.string().nullable().optional(),
    userDate: z.string().nullable().optional(),
    statusId: z.number().int().nullable().optional(),
    statusName: z.string().nullable().optional(),
    coreStatusId: z.number().int().nullable().optional(),
    coreStatusName: z.string().nullable().optional(),
    contingentId: z.number().int().nullable().optional(),
    responsibleUserId: z.number().int().nullable().optional(),
    responsibleUserName: z.string().nullable().optional(),
    responsibleUserJobTitle: z.string().nullable().optional(),
    jobTitleName: z.string().nullable().optional(),
    baselineTaskId: z.number().int().nullable().optional(),
    baselineName: z.string().nullable().optional(),
    isMilestone: z.boolean().default(false),
    hasFiles: z.boolean().default(false),
    lastComment: z.union([lastCommentSchema, z.string()]).nullable().optional(),
    isFollowedByCurrentUser: z.boolean().default(false),
    links: z.array(linkSchema).default([]),
    prereqs: z.array(prereqSchema).default([]),
    steps: z.array(stepSchema).default([]),
    tags: z.array(taskRowTagSchema).default([]),
    projectName: z.string().nullable().optional(),
    phaseName: z.string().nullable().optional(),
    communityName: z.string().nullable().optional(),
    disciplineName: z.string().nullable().optional(),
    departmentName: z.string().nullable().optional(),
    additionalInfo: z.string().nullable().optional(),
    contactName: z.string().nullable().optional(),
    contactEmail: z.string().nullable().optional(),
    anticipatedEffort: z.string().nullable().optional(),
    projectId: z.number().int().nullable().optional(),
    isActive: z.boolean().nullable().optional(),
    isDeprecated: z.boolean().nullable().optional(),
    ordinal: z.number().int().nullable().optional(),
    locationId: z.number().int().nullable().optional(),
});

export const locationTaskDetailsResponseSchema = z.looseObject({
    locationTask: locationTaskSchema,
    locationName: z.string().nullable().optional().default(''),
    locationAbbreviation: z.string().nullable().optional().default(''),
    baselineId: z.number().int().nullable().optional().default(null),
    contingentTaskName: z.string().nullable().optional().default(''),
    contingentTaskStatus: z.string().nullable().optional().default(''),
    contingentTaskCoreStatusId: z.number().int().nullable().optional(),
    jobTitleName: z.string().nullable().optional().default(''),
    successorTasks: z.array(locationTaskSchema).optional().default([]),
    projectName: z.string().nullable().optional().default(''),
    departmentName: z.string().nullable().optional().default(''),
    communityName: z.string().nullable().optional().default(''),
    phaseName: z.string().nullable().optional().default(''),
    projectCoach: z.string().nullable().optional().default(''),
    projectCoachId: z.number().int().nullable().optional().default(null),
    links: z.array(linkSchema).optional().default([]),
    files: z.array(fileSchema).optional().default([]),
    isProjectCertified: z.boolean().optional().default(false),
    steps: z.array(stepSchema).optional().default([]),
    locationKey: z.string().nullable().optional(),
    locationOpeningDate: z.string().nullable().optional(),
    locationTaskTagIds: z.array(z.number().int()).optional().default([]),
    baselineTaskTags: z.array(z.looseObject({
        taskTagId: z.number().int(),
        title: z.string(),
        backgroundColor: z.string().nullable().optional().default(''),
        fontColor: z.string().nullable().optional().default(''),
    })).optional().default([]),
});

// ---- Search ----

export const taskSearchRequestSchema = z.object({
    ...paginationParamsSchema.shape,
    isMobile: z.boolean().default(false),
    locationKeys: z.array(z.string()).default([]),
    name: z.string().default(''),
    taskId: z.string().default(''),
    projectIds: z.array(z.number().int()).default([]),
    targetDateStart: z.string().nullable().optional(),
    targetDateEnd: z.string().nullable().optional(),
    startDateStart: z.string().nullable().optional(),
    startDateEnd: z.string().nullable().optional(),
    responsibleUserName: z.string().default(''),
    statusIds: z.array(z.number().int()).default([]),
    departmentIds: z.array(z.number().int()).default([]),
    disciplineIds: z.array(z.number().int()).default([]),
    communityIds: z.array(z.number().int()).default([]),
    phaseIds: z.array(z.number().int()).default([]),
    tagIds: z.array(z.number().int()).default([]),
    additionalInfo: z.string().default(''),
    baselineName: z.string().default(''),
    recentlyUpdated: z.boolean().nullable().optional(),
    isCalendarView: z.boolean().default(false),
    // Redux holds a boolean, the URL delivers the string 'true'/'false' — isAllLocationsMode() normalizes both
    allLocations: z.union([z.boolean(), z.string()]).nullable().optional(),
    isFollowedByCurrentUser: z.union([z.boolean(), z.string()]).nullable().optional(),
    isMilestone: z.boolean().nullable().optional(),
});

export const taskSearchResponseSchema = z.object({
    ...responseBaseSchema.shape,
    results: z.array(locationTaskSchema),
    totalCount: z.number().int(),
    isMissingActiveLocations: z.boolean().optional().default(false),
    columnOrder: z.array(z.string()).optional(),
});

// ---- List Filters ----

export const listFiltersResponseSchema = z.object({
    isStartDateActive: z.boolean(),
    isTaskDependenciesActive: z.boolean(),
    isTaskStepsActive: z.boolean(),
    isProjectWidgetActive: z.boolean(),
    allowLocationTaskDateChanges: z.boolean(),
    savedColumns: z.array(columnSchema).default([]),
    masterColumns: z.array(columnSchema).default([]),
    calendarStartDay: z.union([z.string(), z.number()]).nullable().optional(),
    ganttZoomLevel: z.string().nullable().optional(),
    ganttExportToPDF: z.boolean().nullable().optional(),
    ganttExportToPNG: z.boolean().nullable().optional(),
    savedColumnOrder: z.array(z.string()).default([]),
    isSaveAndManageTaskFiltersActive: z.boolean().default(false),
    filters: z.array(customFilterSchema).default([]),
});

// ---- Column Filters ----

export const columnFiltersRequestSchema = z.object({
    subscriptionId: z.number().int(),
    locationKeys: z.array(z.string()),
    // "All locations" mode sends no location keys and lets the API span every accessible location
    allLocations: z.boolean().optional(),
});

export const columnFiltersResponseSchema = z.object({
    startDates: z.array(filterOptionSchema).default([]),
    targetDates: z.array(filterOptionSchema).default([]),
    projects: z.array(filterOptionSchema).default([]),
    communities: z.array(filterOptionSchema).default([]),
    disciplines: z.array(filterOptionSchema).default([]),
    departments: z.array(filterOptionSchema).default([]),
    phases: z.array(filterOptionSchema).default([]),
    statuses: z.array(statusFilterOptionSchema).default([]),
    tags: z.array(tagSchema).default([]),
});

// ---- Location Selector Filters ----

// Selector locations are keyed by locationKey only (no numeric id field)
export const selectorLocationSchema = z.object({
    locationKey: z.string(),
    name: z.string(),
    abbreviation: z.string().nullable().optional(),
    openingDate: z.string().nullable().optional(),
    isProject: z.boolean().nullable().optional(),
});

export const locationSelectorFiltersResponseSchema = z.object({
    locations: z.array(selectorLocationSchema).default([]),
    defaultLocationKey: z.string().nullable().default(''),
});

export const initialLocationSelectorFiltersRequestSchema = z.object({
    subscriptionId: z.number().int(),
    locationKeys: z.array(z.string()).optional(),
    defaultLocationKey: z.string().optional(),
});

// ---- Responsible Users ----

export const responsibleUserSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    jobTitle: z.string().nullable().optional(),
});

export const searchResponsibleUsersResponseSchema = z.object({
    users: z.array(responsibleUserSchema),
});

// ---- Comments ----

export const commentSchema = z.looseObject({
    id: z.number().int(),
    locationTaskId: z.number().int().optional(),
    comment: z.string(),
    commentHtml: z.string().nullable().optional().default(''),
    createdByUserId: z.number().int().optional(),
    createdByUserName: z.string().nullable().optional(),
    createdBy: z.string().nullable().optional(),
    createdDate: z.string().nullable().optional(),
    created: z.string().nullable().optional(),
    modifiedDate: z.string().nullable().optional(),
    modified: z.string().nullable().optional(),
    modifiedBy: z.string().nullable().optional(),
    mentions: z.array(z.object({ userId: z.number().int(), userName: z.string() })).optional().default([]),
});

export const commentsResponseSchema = z
    .looseObject({
        comments: z.array(commentSchema).default([]),
        total: z.number().int().optional(),
        totalCount: z.number().int().optional(),
    })
    .transform((response) => ({
        ...response,
        totalCount: response.totalCount ?? response.total ?? 0,
    }));

// ---- History ----

export const historyEntrySchema = z.looseObject({
    id: z.number().int(),
    createdBy: z.string().nullable().optional().default(''),
    created: z.string().nullable().optional(),
    statusNew: z.string().nullable().optional().default(''),
    statusOld: z.string().nullable().optional().default(''),
    responsibleUserNew: z.string().nullable().optional().default(''),
    responsibleUserOld: z.string().nullable().optional().default(''),
    nameNew: z.string().nullable().optional().default(''),
    nameOld: z.string().nullable().optional().default(''),
    additionalInfoNew: z.string().nullable().optional().default(''),
    additionalInfoOld: z.string().nullable().optional().default(''),
    targetDaysOutNew: z.number().int().nullable().optional(),
    targetDaysOutOld: z.number().int().nullable().optional(),
    targetDateNew: z.string().nullable().optional(),
    targetDateOld: z.string().nullable().optional(),
    startDateNew: z.string().nullable().optional(),
    startDateOld: z.string().nullable().optional(),
    budgetNew: z.number().nullable().optional(),
    budgetOld: z.number().nullable().optional(),
    actualNew: z.number().nullable().optional(),
    actualOld: z.number().nullable().optional(),
});

export const historyResponseSchema = z
    .looseObject({
        history: z.array(historyEntrySchema).optional(),
        histories: z.array(historyEntrySchema).optional(),
    })
    .transform((response) => ({
        ...response,
        history: response.history ?? response.histories ?? [],
    }));

// ---- Mentionable Users ----

export const mentionableUserSchema = z.looseObject({
    id: z.number().int(),
    name: z.string().optional(),
    value: z.string().optional(),
    email: z.string().nullable().optional(),
}).transform((user) => ({
    ...user,
    value: user.value ?? user.name ?? '',
}));

export const mentionableUsersResponseSchema = z.object({
    users: z.array(mentionableUserSchema),
});

// ---- Mutations ----

export const updateStatusRequestSchema = z.object({
    locationTaskIds: z.array(z.number().int()),
    statusId: z.number().int(),
});

export const updateResponsibleUserRequestSchema = z.object({
    locationTaskIds: z.array(z.number().int()),
    responsibleUserId: z.number().int().nullable(),
});

export const updateDatesRequestSchema = z.object({
    taskId: z.number().int(),
    startDate: z.string().nullable(),
    targetDate: z.string().nullable(),
});

export const updateIsActiveRequestSchema = z.object({
    locationTaskIds: z.array(z.number().int()),
});

export const updateIsActiveResponseSchema = z.object({
    isError: z.boolean(),
    invalidLocationTasks: z.array(z.object({ id: z.number().int(), name: z.string() })).optional(),
});

export const updateFollowingTasksRequestSchema = z.object({
    locationTaskIds: z.array(z.number().int()),
    unfollow: z.boolean(),
});

export const updateFollowingTasksResponseSchema = z.object({
    isError: z.boolean(),
});

export const updateContingentRequestSchema = z.object({
    taskId: z.number().int(),
    contingentId: z.number().int().nullable(),
});

// Field names must match BulkShiftDates.Command on the API; anything else binds to 0/false.
export const bulkShiftDatesRequestSchema = z.object({
    locationTaskIds: z.array(z.number().int()),
    daysToShift: z.number().int(),
    shiftTargetDates: z.boolean(),
    shiftStartDates: z.boolean(),
});

export const saveCustomFiltersRequestSchema = z.object({
    filterName: z.string().min(1),
    searchParams: z.record(z.string(), z.unknown()),
});

export const importLocationTasksRequestSchema = z.object({
    subscriptionId: z.number().int(),
    locationKey: z.string(),
    file: z.instanceof(File),
});

export const importResultSchema = z.object({
    importErrors: z
        .array(
            z.object({
                rowNumber: z.number().int(),
                columnNumber: z.number().int().nullable().optional(),
                errorMessage: z.string(),
                locationName: z.string().nullable().optional(),
                taskName: z.string().nullable().optional(),
            })
        )
        .default([]),
    successfulImports: z.number().int(),
    totalRows: z.number().int(),
});

export const createLocationTaskRequestSchema = z.object({
    locationKey: z.string(),
    name: z.string().min(1),
    targetDate: z.string().nullable().optional(),
    startDate: z.string().nullable().optional(),
    responsibleUserId: z.number().int().nullable().optional(),
    statusId: z.number().int().nullable().optional(),
    additionalInfo: z.string().nullable().optional(),
    baselineTaskId: z.number().int().nullable().optional(),
    departmentId: z.number().int(),
    communityId: z.number().int(),
    projectId: z.number().int().nullable().optional(),
    contingentId: z.number().int().nullable().optional(),
    successorId: z.number().int().nullable().optional(),
    phaseId: z.number().int().nullable().optional(),
    contactName: z.string().nullable().optional(),
    contactEmail: z.string().nullable().optional(),
    isMilestone: z.boolean().optional().default(false),
    isActive: z.boolean().optional().default(true),
    links: z.array(z.unknown()).optional().default([]),
});

export const updateSavedColumnsRequestSchema = z.object({
    columnIds: z.array(z.number().int()),
});

// ---- Project Coaches ----

export const projectCoachesResponseSchema = z.object({
    projectCoaches: z.array(z.record(z.string(), z.unknown())).default([]),
}).transform((response) => ({
    ...response,
    coaches: response.projectCoaches,
}));

// ---- Location Task Drawer Filters ----

export const locationTaskDrawerFiltersResponseSchema = z.object({
    isStartDateActive: z.boolean().optional().default(false),
    isTaskStepsActive: z.boolean().optional().default(false),
    isTaskFinancialsActive: z.boolean().optional().default(false),
    allowLocationTaskBudgetEntry: z.boolean().optional().default(false),
    defaultNotStartedStatusId: z.number().int().optional().default(0),
    isProjectWidgetActive: z.boolean().optional().default(false),
    isProjectIdRequiredForLocationTask: z.boolean().optional().default(false),
    isTaskDependenciesActive: z.boolean().optional().default(false),
    allowLocationTaskDateChanges: z.boolean().optional().default(false),
    completeStatusId: z.number().int().optional().default(0),
    locationOpeningDate: z.string().nullable().optional().default(''),
    startDates: z.array(filterOptionSchema).default([]),
    targetDates: z.array(filterOptionSchema).default([]),
    projects: z.array(filterOptionSchema).default([]),
    communities: z.array(filterOptionSchema).default([]),
    disciplines: z.array(filterOptionSchema).default([]),
    departments: z.array(filterOptionSchema).default([]),
    phases: z.array(filterOptionSchema).default([]),
    contingentTasks: z.array(z.looseObject({
        id: z.number().int(),
        name: z.string(),
        contingentTaskId: z.number().int().nullable().optional(),
        statusId: z.number().int().nullable().optional(),
        targetDate: z.union([z.string(), z.date()]).nullable().optional(),
    })).default([]),
    responsibleUsers: z.array(filterOptionSchema).default([]),
    statuses: z.array(statusFilterOptionSchema).default([]),
    tags: z.array(tagSchema).default([]),
    taskTags: z.array(z.looseObject({
        id: z.number().int(),
        name: z.string().optional(),
        title: z.string().optional(),
        styles: z.object({
            backgroundColor: z.string().optional(),
            color: z.string().optional(),
        }).nullable().optional(),
    })).default([]),
});

// ---- Feedback ----

export const createFeedbackRequestSchema = z.object({
    locationTaskId: z.number().int(),
    feedbackChoice: z.number().int(),
    text: z.string().nullable().optional(),
});

export const createFeedbackResponseSchema = z.object({
    isError: z.boolean().optional(),
});

// ---- Translation ----

export const validateTranslationRequestSchema = z.object({
    locationTaskId: z.number().int(),
    language: z.string(),
});

export const validateTranslationResponseSchema = z.looseObject({
    canTranslate: z.boolean().optional().default(false),
    labels: z.looseObject({
        language: z.string().optional().default(''),
        translateLabel: z.string().optional().default('Translate'),
        translationRatingLabel: z.union([z.string(), z.number()]).optional().default(0),
    }).optional(),
    locationTask: z.unknown().optional(),
});

export const translateTextRequestSchema = z.object({
    locationTaskId: z.number().int(),
    language: z.string(),
});

export const translateTextResponseSchema = z.looseObject({
    locationTask: z.unknown(),
});

export const translationRatingRequestSchema = z.object({
    translationId: z.union([z.number().int(), z.string()]),
    rating: z.number().int(),
});

export const translationRatingResponseSchema = z.object({
    isError: z.boolean().optional(),
});

// ---- Resource Allocations (workload heatmap) ----

const heatmapPointSchema = z.object({
    x: z.union([z.string(), z.number()]),
    y: z.union([z.number(), z.string()]).nullable(),
});

// `name` is absent for the bucket of tasks without a divider value (e.g. no project)
export const heatmapSeriesSchema = z.object({
    name: z.string().nullable().optional().default(''),
    data: z.array(heatmapPointSchema).default([]),
});

export const resourceAllocationsRequestSchema = z.object({
    ...taskSearchRequestSchema.shape,
    divider: z.string(),
    view: z.string(),
});

export const resourceAllocationsResponseSchema = z.object({
    seriesData: z.array(heatmapSeriesSchema).nullable().default(null),
    startDateRange: z.string().nullable().optional(),
    endDateRange: z.string().nullable().optional(),
    isLastPage: z.boolean().default(true),
});

export const resourceAllocationsDetailsRequestSchema = z.object({
    ...resourceAllocationsRequestSchema.shape,
    seriesName: z.string(),
});

export const resourceAllocationsDetailsResponseSchema = z.object({
    tasks: z.array(z.record(z.string(), z.unknown())).default([]),
});

// ---- Inferred Types ----

export type Link = z.infer<typeof linkSchema>;
export type Prereq = z.infer<typeof prereqSchema>;
export type Step = z.infer<typeof stepSchema>;
export type Tag = z.infer<typeof tagSchema>;
export type FilterOption = z.infer<typeof filterOptionSchema>;
export type StatusFilterOption = z.infer<typeof statusFilterOptionSchema>;
export type TaskColumn = z.infer<typeof columnSchema>;
export type CustomFilter = z.infer<typeof customFilterSchema>;
export type Location = z.infer<typeof locationSchema>;
export type SelectorLocation = z.infer<typeof selectorLocationSchema>;
export type LocationTask = z.infer<typeof locationTaskSchema>;
export type LocationTaskDetailsResponse = z.infer<typeof locationTaskDetailsResponseSchema>;
export type TaskSearchRequest = z.infer<typeof taskSearchRequestSchema>;
export type TaskSearchResponse = z.infer<typeof taskSearchResponseSchema>;
export type ListFiltersResponse = z.infer<typeof listFiltersResponseSchema>;
export type ColumnFiltersRequest = z.infer<typeof columnFiltersRequestSchema>;
export type ColumnFiltersResponse = z.infer<typeof columnFiltersResponseSchema>;
export type LocationSelectorFiltersResponse = z.infer<typeof locationSelectorFiltersResponseSchema>;
export type InitialLocationSelectorFiltersRequest = z.infer<typeof initialLocationSelectorFiltersRequestSchema>;
export type ResponsibleUser = z.infer<typeof responsibleUserSchema>;
export type SearchResponsibleUsersResponse = z.infer<typeof searchResponsibleUsersResponseSchema>;
export type Comment = z.infer<typeof commentSchema>;
export type CommentsResponse = z.infer<typeof commentsResponseSchema>;
export type HistoryEntry = z.infer<typeof historyEntrySchema>;
export type HistoryResponse = z.infer<typeof historyResponseSchema>;
export type MentionableUser = z.infer<typeof mentionableUserSchema>;
export type MentionableUsersResponse = z.infer<typeof mentionableUsersResponseSchema>;
export type UpdateStatusRequest = z.infer<typeof updateStatusRequestSchema>;
export type UpdateResponsibleUserRequest = z.infer<typeof updateResponsibleUserRequestSchema>;
export type UpdateDatesRequest = z.infer<typeof updateDatesRequestSchema>;
export type UpdateIsActiveRequest = z.infer<typeof updateIsActiveRequestSchema>;
export type UpdateIsActiveResponse = z.infer<typeof updateIsActiveResponseSchema>;
export type UpdateFollowingTasksRequest = z.infer<typeof updateFollowingTasksRequestSchema>;
export type UpdateFollowingTasksResponse = z.infer<typeof updateFollowingTasksResponseSchema>;
export type UpdateContingentRequest = z.infer<typeof updateContingentRequestSchema>;
export type BulkShiftDatesRequest = z.infer<typeof bulkShiftDatesRequestSchema>;
export type SaveCustomFiltersRequest = z.infer<typeof saveCustomFiltersRequestSchema>;
export type ImportLocationTasksRequest = z.infer<typeof importLocationTasksRequestSchema>;
export type ImportResult = z.infer<typeof importResultSchema>;
export type CreateLocationTaskRequest = z.infer<typeof createLocationTaskRequestSchema>;
export type UpdateSavedColumnsRequest = z.infer<typeof updateSavedColumnsRequestSchema>;
export type HeatmapSeries = z.infer<typeof heatmapSeriesSchema>;
export type ResourceAllocationsRequest = z.infer<typeof resourceAllocationsRequestSchema>;
export type ResourceAllocationsResponse = z.infer<typeof resourceAllocationsResponseSchema>;
export type ResourceAllocationsDetailsRequest = z.infer<typeof resourceAllocationsDetailsRequestSchema>;
export type ResourceAllocationsDetailsResponse = z.infer<typeof resourceAllocationsDetailsResponseSchema>;
export type ProjectCoachesResponse = z.infer<typeof projectCoachesResponseSchema>;
export type LocationTaskDrawerFiltersResponse = z.infer<typeof locationTaskDrawerFiltersResponseSchema>;
export type CreateFeedbackRequest = z.infer<typeof createFeedbackRequestSchema>;
export type CreateFeedbackResponse = z.infer<typeof createFeedbackResponseSchema>;
export type ValidateTranslationRequest = z.infer<typeof validateTranslationRequestSchema>;
export type ValidateTranslationResponse = z.infer<typeof validateTranslationResponseSchema>;
export type TranslateTextRequest = z.infer<typeof translateTextRequestSchema>;
export type TranslateTextResponse = z.infer<typeof translateTextResponseSchema>;
export type TranslationRatingRequest = z.infer<typeof translationRatingRequestSchema>;
export type TranslationRatingResponse = z.infer<typeof translationRatingResponseSchema>;
